import { useState, useEffect } from 'react';
import { getServerUrl } from '../config/runtimeConfig';

// Helper function to load authenticated images
export const loadAuthenticatedImage = async (imageId, type = 'serve') => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token');
    }

    const endpoint = type === 'thumbnail' ? 'thumbnail' : 'serve';
    const API_BASE_URL = getServerUrl();
    const url = `${API_BASE_URL}/api/images/${imageId}/${endpoint}`;
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to load image: ${response.status}`);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error loading authenticated image:', error);
    return null;
  }
};

// Hook to manage authenticated image URLs
export const useAuthenticatedImage = (imageId, type = 'serve') => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!imageId) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    const loadImage = async () => {
      setLoading(true);
      setError(null);

      try {
        const url = await loadAuthenticatedImage(imageId, type);
        if (url && isMounted) {
          setImageUrl(url);
        } else if (isMounted) {
          setError('Failed to load image');
        }
      } catch (err) {
        if (isMounted) setError(err.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadImage();

    return () => {
      isMounted = false;
    };
  }, [imageId, type]);

  useEffect(() => {
    // Cleanup blob URL only when imageUrl changes
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  return { imageUrl, loading, error };
};
